import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "@/lib/utils"

type ScrollAreaVariant = "default" | "prominent"

type ScrollAreaProps = React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  variant?: ScrollAreaVariant
  fadeEdges?: boolean
}

function ScrollArea({
  className,
  children,
  variant = "default",
  fadeEdges = false,
  type,
  ...props
}: ScrollAreaProps) {
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const [edgeState, setEdgeState] = React.useState({ top: false, bottom: false })
  const isProminent = variant === "prominent"

  const updateEdgeState = React.useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport || !fadeEdges) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = viewport
    const overflow = scrollHeight - clientHeight > 1

    setEdgeState({
      top: overflow && scrollTop > 4,
      bottom: overflow && scrollTop + clientHeight < scrollHeight - 4,
    })
  }, [fadeEdges])

  React.useLayoutEffect(() => {
    updateEdgeState()
  }, [updateEdgeState, children])

  React.useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport || !fadeEdges) {
      return
    }

    viewport.addEventListener("scroll", updateEdgeState, { passive: true })

    const resizeObserver = new ResizeObserver(updateEdgeState)
    resizeObserver.observe(viewport)

    return () => {
      viewport.removeEventListener("scroll", updateEdgeState)
      resizeObserver.disconnect()
    }
  }, [fadeEdges, updateEdgeState])

  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      type={type ?? (isProminent ? "always" : undefined)}
      className={cn("relative", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        data-slot="scroll-area-viewport"
        className="focus-visible:ring-ring/50 size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar variant={variant} />
      <ScrollAreaPrimitive.Corner />
      {fadeEdges && edgeState.top && (
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-background to-transparent",
            isProminent && "right-3"
          )}
        />
      )}
      {fadeEdges && edgeState.bottom && (
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10 bg-gradient-to-t from-background via-background/80 to-transparent",
            isProminent && "right-3"
          )}
        />
      )}
    </ScrollAreaPrimitive.Root>
  )
}

function ScrollBar({
  className,
  orientation = "vertical",
  variant = "default",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> & {
  variant?: ScrollAreaVariant
}) {
  const isProminent = variant === "prominent"

  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none select-none transition-colors",
        orientation === "vertical" &&
          cn(
            "h-full border-l",
            isProminent
              ? "w-3 border-border/40 bg-muted/35 p-0.5"
              : "w-2.5 border-l-transparent p-px"
          ),
        orientation === "horizontal" &&
          cn(
            "flex-col border-t",
            isProminent
              ? "h-3 border-border/40 bg-muted/35 p-0.5"
              : "h-2.5 border-t-transparent p-px"
          ),
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className={cn(
          "relative flex-1 rounded-full transition-colors",
          isProminent
            ? "min-h-10 bg-muted-foreground/45 hover:bg-muted-foreground/65"
            : "bg-border"
        )}
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollArea, ScrollBar }
